import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  PlayCircle, 
  CheckCircle2, 
  Lock,
  Loader2,
  Trophy,
  AlertCircle,
  ChevronRight,
  GraduationCap,
  BookOpen,
  Star,
  Eye,
  EyeOff,
  Play,
  Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { TrainingAttachments } from '../../components/training/TrainingAttachments';

const AreaFormazione = () => {
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [progress, setProgress] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const playerRef = useRef(null);
  const lastValidTimeRef = useRef(0);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    fetchTrainingData();
    loadYouTubeAPI();
  }, []);

  const loadYouTubeAPI = () => {
    if (window.YT && window.YT.Player) {
      return;
    }
    
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  };

  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [videosRes, progressRes] = await Promise.all([
        axios.get(`${API}/api/training/videos`, { headers }),
        axios.get(`${API}/api/training/progress`, { headers })
      ]);

      setVideos(videosRes.data || []);
      setProgress(progressRes.data || {});
    } catch (error) {
      console.error('Error fetching training data:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const isVideoUnlocked = (index) => {
    if (index === 0) return true;
    const prevVideo = videos[index - 1];
    return progress[prevVideo?.id]?.completed === true;
  };

  const handleWatchVideo = (video) => {
    setSelectedVideo(video);
    setQuizMode(false);
    setVideoWatched(false);
    setQuizCompleted(false);
    setQuizQuestions([]);
    setCurrentQuestion(0);
    setQuizScore(0);
    setVideoDuration(0);
    setCurrentTime(0);
    setPlayerReady(false);
    setIsPlaying(false);
    lastValidTimeRef.current = 0;
    
    if (video.video_type === 'upload' && video.video_file_url) {
      // HTML5 video - player initializes via ref after render
      setPlayerReady(true);
    } else {
      // YouTube player
      setTimeout(() => {
        initializePlayer(video.youtube_id);
      }, 100);
    }
  };

  const initializePlayer = (videoId) => {
    // Clean up previous player
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {}
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    const createPlayer = () => {
      playerRef.current = new window.YT.Player('yt-player-container', {
        videoId: videoId,
        playerVars: {
          'autoplay': 0,
          'controls': 1,
          'disablekb': 1,
          'fs': 0,
          'modestbranding': 1,
          'rel': 0,
          'playsinline': 1
        },
        events: {
          'onReady': onPlayerReady,
          'onStateChange': onPlayerStateChange
        }
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      window.onYouTubeIframeAPIReady = createPlayer;
    }
  };

  const onPlayerReady = (event) => {
    const duration = event.target.getDuration();
    setVideoDuration(duration);
    setPlayerReady(true);
    lastValidTimeRef.current = 0;
    
    // Start monitoring playback
    checkIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        const time = playerRef.current.getCurrentTime();
        const duration = playerRef.current.getDuration();
        
        // Anti-skip: if user jumped forward more than 3 seconds, seek back
        if (time > lastValidTimeRef.current + 3) {
          playerRef.current.seekTo(lastValidTimeRef.current, true);
          toast.error('Non puoi saltare avanti! Devi guardare tutto il video.');
          return;
        }
        
        // Update valid time (allow small forward movement during normal playback)
        if (time > lastValidTimeRef.current) {
          lastValidTimeRef.current = time;
        }
        
        setCurrentTime(time);
        
        // Check if video completed (99.5% fallback - YouTube player "ended" state is primary)
        if (time >= duration * 0.995 && duration > 0) {
          setVideoWatched(true);
          setCurrentTime(duration); // Show 100%
          clearInterval(checkIntervalRef.current);
        }
      }
    }, 500);
  };

  const onPlayerStateChange = (event) => {
    // States: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
    if (event.data === 1) {
      setIsPlaying(true);
    } else if (event.data === 2) {
      setIsPlaying(false);
    } else if (event.data === 0) {
      // Video ended - force 100% completion
      setVideoWatched(true);
      setIsPlaying(false);
      setCurrentTime(videoDuration); // Force 100% display
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
      }
    };
  }, []);

  const handleCloseVideo = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {}
    }
    setSelectedVideo(null);
  };

  const handleStartQuiz = async () => {
    if (!selectedVideo || !videoWatched) return;
    
    setLoadingQuiz(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/training/quiz/${selectedVideo.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuizQuestions(response.data.questions);
        setQuizMode(true);
        setCurrentQuestion(0);
        setSelectedAnswer(null);
        setQuizScore(0);
        setQuizCompleted(false);
      } else {
        // No quiz questions available - auto-complete the video
        await handleCompleteWithoutQuiz();
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
      // Quiz not available - auto-complete the video
      await handleCompleteWithoutQuiz();
    } finally {
      setLoadingQuiz(false);
    }
  };

  const handleCompleteWithoutQuiz = async () => {
    if (!selectedVideo || !videoWatched) return;
    setLoadingQuiz(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/training/complete-video`,
        { video_id: selectedVideo.id, score: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizCompleted(true);
      toast.success('Modulo completato! Puoi passare al prossimo video.');
      fetchTrainingData();
    } catch (error) {
      console.error('Error completing video:', error);
      toast.error('Errore nel completamento del modulo');
    } finally {
      setLoadingQuiz(false);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(answerIndex);
    
    if (answerIndex === quizQuestions[currentQuestion].correct_answer) {
      setQuizScore(prev => prev + 1);
    }
  };

  const handleNextQuestion = async () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      setQuizCompleted(true);
      const finalScore = Math.round((quizScore / quizQuestions.length) * 100);
      
      if (finalScore >= 80) {
        try {
          const token = localStorage.getItem('hw_token');
          await axios.post(
            `${API}/api/training/complete-video`,
            { video_id: selectedVideo.id, score: finalScore },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Complimenti! Hai superato il quiz!');
          fetchTrainingData();
        } catch (error) {
          console.error('Error saving progress:', error);
        }
      }
    }
  };

  const getProgressPercentage = () => {
    if (videos.length === 0) return 0;
    const completed = Object.values(progress).filter(p => p.completed).length;
    return Math.round((completed / videos.length) * 100);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-slate-600">Caricamento area formazione...</span>
      </div>
    );
  }

  // Video Player View
  if (selectedVideo) {
    const watchPercentage = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;
    
    return (
      <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-6" data-testid="video-player-view">
        <Button 
          variant="ghost" 
          onClick={handleCloseVideo}
          className="mb-4"
        >
          ← Torna ai video
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video bg-black rounded-t-lg overflow-hidden">
                  {selectedVideo.video_type === 'upload' && selectedVideo.video_file_url ? (
                    <video
                      ref={(el) => {
                        if (el && !el._initialized) {
                          el._initialized = true;
                          el.addEventListener('loadedmetadata', () => {
                            setVideoDuration(el.duration);
                            lastValidTimeRef.current = 0;
                          });
                          el.addEventListener('play', () => setIsPlaying(true));
                          el.addEventListener('pause', () => setIsPlaying(false));
                          el.addEventListener('ended', () => {
                            setVideoWatched(true);
                            setIsPlaying(false);
                            setCurrentTime(el.duration);
                          });
                          // Anti-skip + progress tracking
                          checkIntervalRef.current = setInterval(() => {
                            const time = el.currentTime;
                            const dur = el.duration;
                            if (time > lastValidTimeRef.current + 3) {
                              el.currentTime = lastValidTimeRef.current;
                              toast.error('Non puoi saltare avanti! Devi guardare tutto il video.');
                              return;
                            }
                            if (time > lastValidTimeRef.current) {
                              lastValidTimeRef.current = time;
                            }
                            setCurrentTime(time);
                            if (time >= dur * 0.995 && dur > 0) {
                              setVideoWatched(true);
                              setCurrentTime(dur);
                              clearInterval(checkIntervalRef.current);
                            }
                          }, 500);
                        }
                      }}
                      src={selectedVideo.video_file_url}
                      className="w-full h-full"
                      controls
                      controlsList="nodownload noplaybackrate"
                      disablePictureInPicture
                      data-testid="uploaded-video-player"
                    />
                  ) : (
                    <div id="yt-player-container" className="w-full h-full"></div>
                  )}
                </div>
                <div className="p-4">
                  <h2 className="text-xl font-bold text-slate-800 mb-2">{selectedVideo.title}</h2>
                  <p className="text-slate-600 mb-4">{selectedVideo.description}</p>
                  <TrainingAttachments video={selectedVideo} />
                  
                  {/* Progress indicator */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-600 font-medium">Progresso visualizzazione</span>
                      <span className="font-bold text-purple-600">
                        {formatTime(currentTime)} / {formatTime(videoDuration)}
                      </span>
                    </div>
                    <Progress 
                      value={watchPercentage} 
                      className="h-3"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-slate-500">
                        <AlertCircle className="h-3 w-3 inline mr-1" />
                        {videoWatched 
                          ? (selectedVideo.has_quiz ? '✓ Video completato! Puoi procedere con il quiz.' : '✓ Video completato!')
                          : 'Devi guardare tutto il video fino alla fine. Non puoi saltare avanti.'
                        }
                      </p>
                      <span className="text-xs font-medium text-purple-600">
                        {Math.round(watchPercentage)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quiz Section */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                  Quiz di Verifica
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!quizMode && !quizCompleted && (
                  <div className="text-center space-y-4">
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <AlertCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">
                        {selectedVideo?.has_quiz 
                          ? <>Guarda il video <strong>fino alla fine</strong>, poi completa il quiz per sbloccare il prossimo modulo.</>
                          : <>Guarda il video <strong>fino alla fine</strong> per sbloccare il prossimo modulo.</>
                        }
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        Devi guardare il <strong>100%</strong> del video
                      </p>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${videoWatched ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                      {videoWatched ? (
                        <div className="flex items-center justify-center gap-2">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">Video completato!</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center gap-2">
                            <EyeOff className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              {isPlaying ? 'In riproduzione...' : 'Guarda tutto il video'}
                            </span>
                          </div>
                          <span className="text-xs">
                            Mancano {formatTime(videoDuration - currentTime)}
                          </span>
                        </div>
                      )}
                    </div>

                    {selectedVideo?.has_quiz ? (
                      <Button 
                        onClick={handleStartQuiz}
                        disabled={!videoWatched || loadingQuiz}
                        className={`w-full ${videoWatched ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-300'}`}
                        data-testid="start-quiz-btn"
                      >
                        {loadingQuiz ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Caricamento quiz...
                          </>
                        ) : (
                          <>
                            <BookOpen className="h-4 w-4 mr-2" />
                            {videoWatched ? 'Inizia Quiz' : 'Completa prima il video'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleCompleteWithoutQuiz}
                        disabled={!videoWatched || loadingQuiz}
                        className={`w-full ${videoWatched ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-300'}`}
                        data-testid="complete-no-quiz-btn"
                      >
                        {loadingQuiz ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Completamento...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            {videoWatched ? 'Completa modulo' : 'Completa prima il video'}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {quizMode && !quizCompleted && quizQuestions.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                      <span>Domanda {currentQuestion + 1} di {quizQuestions.length}</span>
                      <span>{quizScore}/{currentQuestion + (selectedAnswer !== null ? 1 : 0)} corrette</span>
                    </div>
                    <Progress value={((currentQuestion + 1) / quizQuestions.length) * 100} className="h-2" />

                    <div className="mt-4">
                      <p className="font-medium text-slate-800 mb-4">
                        {quizQuestions[currentQuestion].question}
                      </p>
                      <div className="space-y-2">
                        {quizQuestions[currentQuestion].options.map((option, idx) => {
                          let buttonClass = 'w-full text-left p-3 rounded-lg border transition-colors ';
                          if (selectedAnswer === null) {
                            buttonClass += 'hover:bg-slate-50 border-slate-200';
                          } else if (idx === quizQuestions[currentQuestion].correct_answer) {
                            buttonClass += 'bg-green-100 border-green-500 text-green-800';
                          } else if (idx === selectedAnswer) {
                            buttonClass += 'bg-red-100 border-red-500 text-red-800';
                          } else {
                            buttonClass += 'border-slate-200 opacity-50';
                          }
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleAnswerSelect(idx)}
                              className={buttonClass}
                              disabled={selectedAnswer !== null}
                              data-testid={`quiz-option-${idx}`}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {selectedAnswer !== null && (
                      <Button 
                        onClick={handleNextQuestion}
                        className="w-full mt-4"
                        data-testid="next-question-btn"
                      >
                        {currentQuestion < quizQuestions.length - 1 ? 'Prossima Domanda' : 'Vedi Risultato'}
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                )}

                {quizCompleted && (
                  <div className="text-center space-y-4">
                    <div className={`p-6 rounded-lg ${quizScore / quizQuestions.length >= 0.8 ? 'bg-green-50' : 'bg-orange-50'}`}>
                      {quizScore / quizQuestions.length >= 0.8 ? (
                        <>
                          <Trophy className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                          <h3 className="text-xl font-bold text-green-800">Complimenti!</h3>
                          <p className="text-green-700">Hai superato il quiz!</p>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-3" />
                          <h3 className="text-xl font-bold text-orange-800">Riprova!</h3>
                          <p className="text-orange-700">Devi ottenere almeno l'80%</p>
                        </>
                      )}
                      <p className="text-2xl font-bold mt-3">
                        {quizScore}/{quizQuestions.length} 
                        <span className="text-sm font-normal ml-2">
                          ({Math.round((quizScore / quizQuestions.length) * 100)}%)
                        </span>
                      </p>
                    </div>
                    {quizScore / quizQuestions.length < 0.8 && (
                      <Button 
                        onClick={() => {
                          setQuizMode(false);
                          setQuizCompleted(false);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Riprova Quiz
                      </Button>
                    )}
                    <Button 
                      onClick={handleCloseVideo}
                      className="w-full"
                    >
                      Torna ai Video
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Videos List View
  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-6" data-testid="area-formazione">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Area Formazione</h1>
        <p className="text-slate-500 mt-1">Completa i moduli formativi per migliorare le tue competenze</p>
      </div>

      {/* Overall Progress */}
      <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Progresso Totale</h2>
              <p className="text-purple-100 text-sm">
                {Object.values(progress).filter(p => p.completed).length} di {videos.length} moduli completati
              </p>
            </div>
            <div className="text-right">
              <span className="text-4xl font-bold">{getProgressPercentage()}%</span>
            </div>
          </div>
          <Progress 
            value={getProgressPercentage()} 
            className="h-3 bg-purple-400"
          />
        </CardContent>
      </Card>

      {/* Videos List */}
      {videos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-slate-500">
            <PlayCircle className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-xl font-medium">Nessun video disponibile</p>
            <p className="text-sm mt-2">I video formativi saranno disponibili a breve</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {videos.map((video, idx) => {
            const unlocked = isVideoUnlocked(idx);
            const completed = progress[video.id]?.completed;
            const score = progress[video.id]?.score;

            return (
              <Card 
                key={video.id || idx}
                className={`transition-all ${unlocked ? 'hover:shadow-lg cursor-pointer' : 'opacity-60'}`}
                onClick={() => unlocked && handleWatchVideo(video)}
                data-testid={`video-card-${video.id || idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Thumbnail */}
                    {(video.thumbnail || (video.video_type !== 'upload' && video.youtube_id)) && (
                      <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                        <img
                          src={video.thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {/* Video Number/Status */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      completed ? 'bg-green-100 text-green-600' : 
                      unlocked ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {completed ? (
                        <CheckCircle2 className="h-6 w-6" />
                      ) : unlocked ? (
                        <span className="text-lg font-bold">{idx + 1}</span>
                      ) : (
                        <Lock className="h-5 w-5" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          Modulo {idx + 1}
                        </Badge>
                        {completed && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            {score}%
                          </Badge>
                        )}
                        {video.has_quiz && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            Quiz pronto
                          </Badge>
                        )}
                        {(video.attachments?.length || 0) > 0 && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700" data-testid={`video-attachment-count-${video.id}`}>
                            <Paperclip className="h-3 w-3 mr-1" />
                            {video.attachments.length}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-bold text-lg text-slate-800 mb-1 truncate">{video.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-1">{video.description}</p>
                      {video.duration && (
                        <p className="text-xs text-slate-400 mt-1">Durata: {video.duration}</p>
                      )}
                    </div>

                    {/* Action */}
                    {unlocked && !completed && (
                      <Button size="sm" className="ml-4 bg-purple-600 hover:bg-purple-700 flex-shrink-0">
                        <PlayCircle className="h-4 w-4 mr-1" />
                        Guarda
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AreaFormazione;
